<script setup lang="ts">
/**
 * ModulePage —— cheng-creat 新增分栏页面的数据库驱动外壳。
 *
 * 页面优先识别 `Section.type = module:<routeName>` 与 `bodyHtml` 根节点上的
 * `data-module-id`；富文本编辑器剥掉该属性时，按既定模块顺序恢复路由 key。
 * 目录、正文、图片和附件都来自 Public Aggregate；不把申报书/总结报告正文硬编码在前台。
 */
import { computed, nextTick, onMounted, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import RichTextViewer from '@/components/RichTextViewer.vue';

import CriticalFailureBanner from '@/components/CriticalFailureBanner.vue';
import MaterialList from '@/components/MaterialList.vue';
import { copy } from '@/i18n/zh-CN';
import {
  moduleRouteLocation,
  moduleSections,
  splitModuleTitle,
  type ModuleRouteName,
} from '@/composables/module-target';
import { useShowcase } from '@/store/use-showcase';
import { hasText } from '@/registry/sections/section-presenters';

const props = defineProps<{
  readonly routeName: ModuleRouteName;
}>();

const store = useShowcase();
const route = useRoute();
const router = useRouter();

onMounted(() => {
  if (store.state.status === 'idle') {
    void store.bootstrap();
  }
});

const entries = computed(() => moduleSections(store.state.aggregate?.sections ?? [], props.routeName));
const routeModuleId = computed(() => {
  const value = route.params.moduleId;
  return typeof value === 'string' ? value : null;
});
const currentEntry = computed(() => {
  const requested = routeModuleId.value;
  if (requested !== null) {
    return entries.value.find((entry) => entry.moduleId === requested) ?? null;
  }
  return entries.value[0] ?? null;
});
const pageTitle = computed(() => currentEntry.value?.section.title ?? copy.app.title);
const pageTitleParts = computed(() => splitModuleTitle(pageTitle.value));
const directoryEntries = computed(() =>
  entries.value.map((entry) => ({
    ...entry,
    titleParts: splitModuleTitle(entry.section.title),
  })),
);
const previewToken = computed(() => store.state.previewToken);
const pdfCacheVersion = computed(() => store.state.contentVersion);
const isLoading = computed(
  () => store.state.status === 'idle' || store.state.status === 'loading',
);

watch(
  [entries, routeModuleId],
  ([nextEntries, requested]) => {
    if (store.state.status !== 'ready' && store.state.status !== 'revalidating') {
      return;
    }
    const first = nextEntries[0];
    if (first === undefined) {
      return;
    }
    if (requested === first.moduleId || nextEntries.some((entry) => entry.moduleId === requested)) {
      return;
    }
    void router.replace(moduleRouteLocation(props.routeName, first.moduleId, {
      anchor: routeHashAnchor(),
      previewToken: previewToken.value,
    }));
  },
  { immediate: true },
);

watch(
  [currentEntry, () => route.hash],
  () => {
    void scrollToRouteHash();
  },
  { flush: 'post', immediate: true },
);

function hrefFor(moduleId: string): string {
  return router.resolve(moduleRouteLocation(props.routeName, moduleId, {
    previewToken: previewToken.value,
  })).href;
}

function isActive(moduleId: string): boolean {
  return currentEntry.value?.moduleId === moduleId;
}

function onDirectoryClick(event: MouseEvent, moduleId: string): void {
  event.preventDefault();
  void router.replace(moduleRouteLocation(props.routeName, moduleId, {
    previewToken: previewToken.value,
  }));
}

function routeHashAnchor(): string | undefined {
  const raw = route.hash.startsWith('#') ? route.hash.slice(1) : '';
  if (raw === '') {
    return undefined;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

async function scrollToRouteHash(): Promise<void> {
  if (currentEntry.value === null) {
    return;
  }
  const anchor = routeHashAnchor();
  if (anchor === undefined) {
    return;
  }
  await nextTick();
  const target = document.getElementById(anchor);
  if (target === null) {
    // eslint-disable-next-line no-console
    console.warn(`[ModulePage] hash target not found: "${anchor}"`);
    return;
  }
  target.scrollIntoView();
}
</script>

<template>
  <main :class="$style.page" :data-testid="`${routeName}-page`">
    <div
      v-if="isLoading"
      :class="$style.skeleton"
      data-testid="module-page-skeleton"
    >
      {{ copy.app.loading }}
    </div>

    <CriticalFailureBanner
      v-else-if="store.state.status === 'failed'"
      :error="store.state.error"
    />

    <template v-else>
      <section :class="$style.banner" data-shell="page-banner" :data-testid="`${routeName}-banner`">
        <RouterLink :class="$style.backLink" to="/" :data-testid="`${routeName}-back-home`">
          {{ copy.detail.backHome }}
        </RouterLink>
        <h1 :class="$style.bannerTitle">
          {{ pageTitleParts.main }}
          <span v-if="pageTitleParts.sub !== null" :class="$style.titleSub">
            <template v-for="(line, lineIndex) in pageTitleParts.subLines" :key="lineIndex">
              <template v-if="lineIndex > 0"><br /></template>{{ line }}
            </template>
          </span>
        </h1>
      </section>

      <div :class="$style.layout">
        <aside :class="$style.sidebar" :data-testid="`${routeName}-directory`">
          <div :class="$style.sidebarTitle">{{ copy.detail.sidebarTitle }}</div>
          <ul :class="$style.sidebarList">
            <li v-for="entry in directoryEntries" :key="entry.moduleId">
              <a
                :class="[$style.sidebarLink, isActive(entry.moduleId) ? $style.sidebarLinkActive : null]"
                :href="hrefFor(entry.moduleId)"
                :aria-current="isActive(entry.moduleId) ? 'page' : undefined"
                :data-testid="`${routeName}-directory-${entry.moduleId}`"
                @click="onDirectoryClick($event, entry.moduleId)"
              >
                {{ entry.titleParts.main }}
                <span v-if="entry.titleParts.sub !== null" :class="$style.titleSub">
                  <template v-for="(line, lineIndex) in entry.titleParts.subLines" :key="lineIndex">
                    <template v-if="lineIndex > 0"><br /></template>{{ line }}
                  </template>
                </span>
              </a>
            </li>
          </ul>
        </aside>

        <section :class="$style.content" :data-testid="`${routeName}-content`">
          <article v-if="currentEntry !== null" :data-testid="`${routeName}-section-${currentEntry.moduleId}`">
            <h2 :class="$style.sectionTitle">
              {{ pageTitleParts.main }}
              <span v-if="pageTitleParts.sub !== null" :class="$style.titleSub">
                <template v-for="(line, lineIndex) in pageTitleParts.subLines" :key="lineIndex">
                  <template v-if="lineIndex > 0"><br /></template>{{ line }}
                </template>
              </span>
            </h2>
            <p v-if="hasText(currentEntry.section.summary)" :class="$style.summary">
              {{ currentEntry.section.summary }}
            </p>
            <div v-if="hasText(currentEntry.section.bodyHtml)" :class="$style.richText">
              <RichTextViewer
                :html="currentEntry.section.bodyHtml"
                :preview-token="previewToken"
                :pdf-cache-version="pdfCacheVersion"
                as="article"
              />
            </div>
            <div v-else :class="$style.empty" data-testid="module-page-empty" />
            <MaterialList
              v-if="currentEntry.section.materials.length > 0"
              :class="$style.material"
              :materials="currentEntry.section.materials"
              :preview-token="previewToken"
              element-id="materials"
            />
          </article>
          <div v-else :class="$style.empty" data-testid="module-page-empty" />
        </section>
      </div>
    </template>
  </main>
</template>

<style module src="./ModulePage.module.css"></style>
